import { Lock } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { ClassProblemLine, useClassSetter } from '../detail/ClassControl';
import { classOf, INTERNAL_CLASSES, type InternalClass } from '../internal-class';
import type { ReportExpenseRow } from './types';

/** True when the note's rows carry the class: the caller is Gestione or one of its approvers. */
export function rowsClassified(rows: readonly ReportExpenseRow[]): boolean {
  return rows.some((row) => classOf(row) !== undefined);
}

/**
 * The approver's class on the decision page (U1 board 2.6, #952 L12): the expense page's segments
 * («Nessuna», A1…PD), compact, per row, saved at once without opening the expense. The chosen
 * segment wears the selected state (S1). On the phone's cards it has a visible label, since there
 * is no column heading there. Shown only where the row carries the class.
 */
export function RowClass({ row, name }: { readonly row: ReportExpenseRow; readonly name: string }) {
  const { t } = useTranslation('expenses-parity');
  const group = useId();
  const initial = classOf(row);
  const [saved, setSaved] = useState(initial ?? null);
  const setter = useClassSetter<object>(row.id, saved, (view) => setSaved(classOf(view) ?? null));
  if (initial === undefined) return null;
  const options: readonly (InternalClass | null)[] = [null, ...INTERNAL_CLASSES];
  return (
    <span className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs @min-[620px]/shell:sr-only" aria-hidden>
        {t('class.columnLong')}
      </span>
      <span
        role="radiogroup"
        aria-label={t('class.rowLabel', { expense: name })}
        aria-busy={setter.busy}
        className="border-border inline-flex w-max overflow-hidden rounded-md border"
      >
        {options.map((option) => {
          const label = option ?? t('class.none');
          const checked = setter.shown === option;
          return (
            <label
              key={label}
              className={cn(
                `border-border flex min-h-11 min-w-11 cursor-pointer items-center justify-center
                border-l px-2 text-xs font-semibold whitespace-nowrap first:border-l-0
                has-[:focus-visible]:ring-2 @min-[620px]/shell:min-h-8 @min-[620px]/shell:min-w-8`,
                checked ? 'bg-selected text-selected-foreground' : 'hover:bg-muted',
              )}
            >
              <input
                type="radio"
                className="sr-only"
                name={group}
                checked={checked}
                disabled={setter.busy}
                onChange={() => void setter.choose(option)}
              />
              {label}
            </label>
          );
        })}
      </span>
      <ClassProblemLine problem={setter.problem} />
    </span>
  );
}

/** Above the rows: who sees the class column (board 2.6). */
export function RowClassHint({ rows }: { readonly rows: readonly ReportExpenseRow[] }) {
  const { t } = useTranslation('expenses-parity');
  if (!rowsClassified(rows)) return null;
  return (
    <p className="text-muted-foreground flex items-center gap-1 text-sm">
      <Lock className="size-3.5" aria-hidden />
      {t('class.approverHint')}
    </p>
  );
}
